import localforage from 'localforage'
import { trpcClient } from '@/lib/trpc'
import type {
  Comment,
  CreateCommentInput,
  CommentCache,
  CommentDraft,
  PendingOperation,
  CommentStatus,
  CommentScope,
  CommentFilters,
} from './types'

export class CommentStorageService {
  private _storage: LocalForage

  constructor() {
    this._storage = localforage.createInstance({
      name: 'screenata-comments',
    })
  }

  // Getter for storage keys method
  async getStorageKeys() {
    return this._storage.keys()
  }

  async loadComments(
    docId: string,
    forceRefresh: boolean = false,
    filters?: CommentFilters
  ): Promise<Comment[]> {
    const cacheKey = this.buildCacheKey(docId, filters)

    // 1. Try local storage first (unless forced refresh)
    if (!forceRefresh) {
      const cached = await this._storage.getItem<CommentCache>(cacheKey)
      if (cached && Date.now() - cached.fetchedAt < 5 * 60 * 1000) {
        // 5 min cache
        console.log('Using cached comments')
        return cached.comments
      }
    }

    // 2. Fetch from server
    try {
      const response = await trpcClient.comments.getComments.query({
        docId,
        includeReplies: true,
        includeResolved: filters?.includeResolved ?? false,
        branchId: filters?.branchId,
        changeRequestId: filters?.changeRequestId,
        policySubmissionId: filters?.policySubmissionId,
        scope: filters?.scope,
        source: filters?.source,
      })

      const comments = response as Comment[]

      // Cache for offline access
      await this._storage.setItem(cacheKey, {
        comments,
        fetchedAt: Date.now(),
      })

      return comments
    } catch (error: any) {
      // Offline fallback
      const cached = await this._storage.getItem<CommentCache>(cacheKey)
      if (cached) {
        console.warn('Using cached comments due to network error')
        return cached.comments
      }
      throw error
    }
  }

  async createComment(
    input: CreateCommentInput & { id: string }
  ): Promise<Comment> {
    try {
      const savedComment = await trpcClient.comments.createComment.mutate(
        input as any
      )
      await this.addToLocalCache(input.docId, savedComment as Comment, {
        branchId: input.branchId,
        changeRequestId: input.changeRequestId,
        policySubmissionId: input.policySubmissionId,
      })
      return savedComment as Comment
    } catch (error: any) {
      // Persist as pending create with same final id
      await this._storage.setItem(`pending:${input.docId}`, {
        tempId: input.id,
        operation: 'create',
        data: input,
        error: error.message,
      })
      throw error
    }
  }

  async resolveComment(
    commentId: string,
    resolution: CommentStatus
  ): Promise<void> {
    try {
      await trpcClient.comments.resolveComment.mutate({
        id: commentId,
        resolution: resolution as any,
      })
    } catch (error: any) {
      console.error('Failed to resolve comment:', error)
      // Store as pending operation for retry
      const cached = await this._storage.getItem<CommentCache>(
        `comments:${commentId}`
      )
      if (cached) {
        await this._storage.setItem(`pending:resolve:${commentId}`, {
          operation: 'resolve',
          data: { id: commentId, resolution },
          error: error.message,
        })
      }
      throw error
    }
  }

  async deleteComment(
    commentId: string,
    options?: { skipRemote?: boolean }
  ): Promise<void> {
    try {
      if (!options?.skipRemote) {
        await trpcClient.comments.deleteComment.mutate({
          id: commentId,
          deleteReplies: true,
        })
      }
    } catch (error: any) {
      console.error('Failed to delete comment:', error)
      // Store as pending operation for retry
      await this._storage.setItem(`pending:delete:${commentId}`, {
        operation: 'delete',
        data: { id: commentId },
        error: error.message,
      })
      throw error
    }
  }

  async updateComment(commentId: string, content: string, metadata?: any): Promise<Comment> {
    try {
      const updatedComment = await trpcClient.comments.updateComment.mutate({
        id: commentId,
        content,
        metadata,
      })
      return updatedComment as Comment
    } catch (error: any) {
      console.error('Failed to update comment:', error)
      throw error
    }
  }

  async createReply(
    parentId: string,
    content: string,
    idOrOptions?: string | { id?: string; mentions?: string[]; richContent?: any }
  ): Promise<Comment> {
    const id = typeof idOrOptions === 'string' ? idOrOptions : idOrOptions?.id
    const mentions = typeof idOrOptions === 'object' ? idOrOptions?.mentions : undefined
    const richContent = typeof idOrOptions === 'object' ? idOrOptions?.richContent : undefined
    const finalId = id || `c${Date.now()}${Math.random().toString(36).slice(2, 8)}`

    // Get parent comment to inherit workspace/doc context
    let parentComment: Comment | null = null
    const keys = await this._storage.keys()
    const commentKeys = keys.filter((k) => k.startsWith('comments:'))

    for (const key of commentKeys) {
      const cached = await this._storage.getItem<CommentCache>(key)
      if (cached) {
        const found = cached.comments.find((c) => c.id === parentId)
        if (found) {
          parentComment = found
          break
        }
      }
    }

    const reply: Comment = {
      id: finalId,
      commentType: 'REPLY',
      scope: 'PAGE' as CommentScope, // Replies inherit parent's scope context
      docId: parentComment?.docId || '',
      parentId,
      content,
      source: 'USER',
      type: 'COMMENT',
      commentStatus: 'OPEN',
      visibility: 'WORKSPACE',
      branchId: parentComment?.branchId || undefined,
      changeRequestId: parentComment?.changeRequestId || undefined,
      policySubmissionId: parentComment?.policySubmissionId || undefined,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    // Update local cache immediately with the final id
    if (parentComment?.docId) {
      await this.addToLocalCache(parentComment.docId, reply, {
        branchId: parentComment.branchId || undefined,
        changeRequestId: parentComment.changeRequestId || undefined,
        policySubmissionId: parentComment.policySubmissionId || undefined,
      })
    }

    try {
      const savedReply = await trpcClient.comments.createReply.mutate({
        id: finalId,
        parentId,
        content,
        mentions: mentions || [],
        richContent: richContent,
      } as any)
      // Cache already has final id; nothing to replace
      // Ensure the reply has the correct commentType
      return { ...savedReply, commentType: 'REPLY' } as Comment
    } catch (error: any) {
      console.error('Failed to create reply:', error)

      // Mark as pending sync for offline support
      await this._storage.setItem(`pending:reply:${finalId}`, {
        tempId: finalId,
        operation: 'create',
        data: { id: finalId, parentId, content, mentions, richContent },
        error: error.message,
      } as PendingOperation)

      // Return temp reply for UI with sync error flag
      return { ...reply, metadata: { syncError: true } }
    }
  }

  // Simple draft support
  async saveDraft(
    docId: string,
    scope: CommentScope,
    content: string
  ): Promise<void> {
    const key = `draft:${docId}:${scope}`
    await this._storage.setItem(key, {
      content,
      savedAt: Date.now(),
    })
  }

  async getDraft(docId: string, scope: CommentScope): Promise<string | null> {
    const draft = await this._storage.getItem<CommentDraft>(
      `draft:${docId}:${scope}`
    )

    // Auto-expire old drafts (7 days)
    if (draft && Date.now() - draft.savedAt < 7 * 24 * 60 * 60 * 1000) {
      return draft.content
    }

    return null
  }

  async deleteDraft(docId: string, scope: CommentScope): Promise<void> {
    await this._storage.removeItem(`draft:${docId}:${scope}`)
  }

  async clearCommentsCache(
    docId: string,
    branchId?: string,
    changeRequestId?: string,
    policySubmissionId?: string
  ): Promise<void> {
    // Clear all possible cache keys for this document
    const keys = await this._storage.keys()
    const docCacheKeys = keys.filter((k) => k.startsWith(`comments:${docId}`))

    for (const key of docCacheKeys) {
      await this._storage.removeItem(key)
    }

    // Also clear the specific cache key if provided
    if (branchId) {
      const cacheKey = this.buildCacheKey(docId, {
        docId,
        branchId,
        changeRequestId,
        policySubmissionId,
      })
      await this._storage.removeItem(cacheKey)
    }
  }

  // Change Request comment merge handling
  async handleCommentMerge(
    changeRequestId: string,
    targetBranchId: string,
    mergeStrategy: 'merge_all' | 'resolve_all' | 'selective',
    selectedCommentIds?: string[]
  ): Promise<void> {
    try {
      await trpcClient.comments.mergeComments.mutate({
        changeRequestId,
        targetBranchId,
        mergeStrategy,
        selectedCommentIds,
      })

      // Clear caches for both source and target branches
      // This ensures fresh data is loaded next time
      await this.clearAllCaches()
    } catch (error) {
      console.error('Failed to merge comments:', error)
      throw error
    }
  }

  // Clear all comment caches (useful after merges)
  async clearAllCaches(): Promise<void> {
    const keys = await this._storage.keys()
    const commentKeys = keys.filter((k) => k.startsWith('comments:'))

    for (const key of commentKeys) {
      await this._storage.removeItem(key)
    }
  }

  // Get count of pending operations
  async getPendingOperationsCount(): Promise<number> {
    const keys = await this._storage.keys()
    const pendingKeys = keys.filter((k) => k.startsWith('pending:'))
    return pendingKeys.length
  }

  // Get all pending operations for debugging/UI display
  async getPendingOperations(): Promise<
    Array<{ key: string; operation: PendingOperation }>
  > {
    const keys = await this._storage.keys()
    const pendingKeys = keys.filter((k) => k.startsWith('pending:'))

    const operations: Array<{ key: string; operation: PendingOperation }> = []
    for (const key of pendingKeys) {
      const operation = await this._storage.getItem<PendingOperation>(key)
      if (operation) {
        operations.push({ key, operation })
      }
    }

    return operations
  }

  // Enhanced sync for all types of pending operations
  async syncAllPending(): Promise<void> {
    const keys = await this._storage.keys()
    const pendingKeys = keys.filter((k) => k.startsWith('pending:'))

    for (const key of pendingKeys) {
      const parts = key.split(':')
      const operation = parts[1]

      try {
        if (operation === 'create') {
          // Handle create operations
          const docId = parts[2]
          await this.syncPending(docId)
        } else if (operation === 'resolve') {
          // Handle resolve operations
          const commentId = parts[2]
          const pending = await this._storage.getItem<PendingOperation>(key)
          if (pending?.data) {
            await trpcClient.comments.resolveComment.mutate(pending.data)
            await this._storage.removeItem(key)
          }
        } else if (operation === 'delete') {
          // Handle delete operations
          const commentId = parts[2]
          const pending = await this._storage.getItem<PendingOperation>(key)
          if (pending?.data) {
            await trpcClient.comments.deleteComment.mutate({
              id: pending.data.id,
              deleteReplies: false,
            })
            await this._storage.removeItem(key)
          }
        } else if (operation === 'reply') {
          // Handle reply operations
          const tempId = parts[2]
          const pending = await this._storage.getItem<PendingOperation>(key)
          if (pending?.data) {
            const savedReply = await trpcClient.comments.createReply.mutate(
              pending.data
            )
            // Find and replace temp reply in cache
            const cacheKeys = await this._storage.keys()
            const commentCacheKeys = cacheKeys.filter((k) =>
              k.startsWith('comments:')
            )
            for (const cacheKey of commentCacheKeys) {
              const cached = await this._storage.getItem<CommentCache>(cacheKey)
              if (cached) {
                const tempIndex = cached.comments.findIndex(
                  (c) => c.id === tempId
                )
                if (tempIndex !== -1) {
                  cached.comments[tempIndex] = {
                    ...savedReply,
                    commentType: 'REPLY',
                  } as Comment
                  await this._storage.setItem(cacheKey, cached)
                  break
                }
              }
            }
            await this._storage.removeItem(key)
          }
        }
      } catch (error) {
        console.error(`Failed to sync pending ${operation} operation:`, error)
        // Keep as pending, will retry on next online event
      }
    }
  }

  // Simple sync for pending operations
  async syncPending(docId: string): Promise<void> {
    const pending = await this._storage.getItem<PendingOperation>(
      `pending:${docId}`
    )
    if (!pending) return

    try {
      const result = await trpcClient.comments.createComment.mutate(
        pending.data
      )
      await this.replaceTempComment(docId, pending.tempId, result as Comment)
      await this._storage.removeItem(`pending:${docId}`)
    } catch (error) {
      console.error('Failed to sync pending comment:', error)
      // Keep as pending, user can retry manually
    }
  }

  // Helper methods
  private async addToLocalCache(
    docId: string,
    comment: Comment,
    filters?: {
      branchId?: string
      changeRequestId?: string
      policySubmissionId?: string
    }
  ): Promise<void> {
    const cacheKey = this.buildCacheKey(docId, { docId, ...filters })
    const cached = await this._storage.getItem<CommentCache>(cacheKey)
    const comments = cached?.comments || []

    await this._storage.setItem(cacheKey, {
      comments: [...comments, comment],
      fetchedAt: cached?.fetchedAt || Date.now(),
    })
  }

  private async replaceTempComment(
    docId: string,
    tempId: string,
    realComment: Comment
  ): Promise<void> {
    const cacheKey = this.buildCacheKey(docId, {
      docId,
      branchId: realComment.branchId || undefined,
      changeRequestId: realComment.changeRequestId || undefined,
      policySubmissionId: realComment.policySubmissionId || undefined,
    })
    const cached = await this._storage.getItem<CommentCache>(cacheKey)
    if (!cached) return

    const comments = cached.comments.map((c) =>
      c.id === tempId ? realComment : c
    )

    await this._storage.setItem(cacheKey, {
      comments,
      fetchedAt: cached.fetchedAt,
    })
  }

  private buildCacheKey(docId: string, filters?: CommentFilters): string {
    const segments = [`comments:${docId}`]
    if (filters?.branchId) {
      segments.push(filters.branchId)
    }
    if (filters?.changeRequestId) {
      segments.push(`cr:${filters.changeRequestId}`)
    }
    if (filters?.policySubmissionId) {
      segments.push(`submission:${filters.policySubmissionId}`)
    }
    return segments.join(':')
  }
}

// Singleton instance
let storageInstance: CommentStorageService

export function getCommentStorage(): CommentStorageService {
  if (!storageInstance) {
    storageInstance = new CommentStorageService()
  }
  return storageInstance
}

// Simple online/offline handling
if (typeof window !== 'undefined') {
  window.addEventListener('online', async () => {
    const storage = getCommentStorage()

    // Try to sync all pending operations
    await storage.syncAllPending()
  })

  // Also check on visibility change (tab becomes active)
  document.addEventListener('visibilitychange', async () => {
    if (!document.hidden && navigator.onLine) {
      const storage = getCommentStorage()
      await storage.syncAllPending()
    }
  })
}
